<script lang="ts">
import { ActionsMenuIcon } from "@/components/newUi/icons/actionsMenuIcon";
import {
  Comment,
  defineComponent,
  Fragment,
  getCurrentInstance,
  h,
  onBeforeUnmount,
  onMounted,
  onUpdated,
  reactive,
  shallowRef,
  Text,
  type VNode,
} from "vue";

import { useI18n } from "vue-i18n";
import { useNewUiEnabled } from "@/components/newUi/core/useNewUiEnabled";
import UiDropdown from "@/components/newUi/buttons/UiDropdown.vue";
import UiWrapIf from "@/components/newUi/layout/UiWrapIf.vue";

const MAX_INLINE_ACTIONS = 1;

const countActions = (nodes: VNode[]): number =>
  nodes.reduce((count, node) => {
    if (node.type === Comment) {
      return count;
    }

    if (node.type === Text) {
      return typeof node.children === "string" && node.children.trim() ? count + 1 : count;
    }

    if (node.type === Fragment) {
      return count + countActions(Array.isArray(node.children) ? (node.children as VNode[]) : []);
    }

    if (node.type === UiWrapIf && !node.props?.when) {
      const children = node.children as { default?: () => VNode[] } | null;

      return count + countActions(children?.default?.() ?? []);
    }

    return count + 1;
  }, 0);

const tableActionCounts = new WeakMap<Element, Map<symbol, number>>();

const getTableActionCounts = (table: Element): Map<symbol, number> => {
  let counts = tableActionCounts.get(table);

  if (!counts) {
    counts = reactive(new Map<symbol, number>()) as Map<symbol, number>;
    tableActionCounts.set(table, counts);
  }

  return counts;
};

// Wraps the legacy action buttons of a table row. With the flag off the slot is rendered as is.
// With the flag on a lone action stays inline and two or more go into an "Actions" dropdown.
// Once any row of a table collapses into the dropdown, every row of that table does, so the column looks the same all the way down.
// Pass the buttons as direct slot children: a wrapper element counts as a single action.
// Slot children must be plain action buttons: the dropdown unmounts them as soon as it closes, so modals belong outside.
export default defineComponent({
  name: "UiRowActions",

  props: {
    // With the flag off, wraps the buttons in a div (with this class, if any) when the old markup had one; null means no wrapper.
    legacyClass: {
      type: String,
      default: null,
    },
  },

  setup(props, { slots }) {
    const newUiEnabled = useNewUiEnabled();
    const { t } = useI18n();
    const instance = getCurrentInstance();
    const rowId = Symbol("row-actions");

    let actionsCount = 0;
    const tableCounts = shallowRef<Map<symbol, number> | null>(null);

    const publishActionsCount = (): void => {
      const table = (instance?.proxy?.$el as Element | null)?.closest?.("table") ?? null;

      if (!table) {
        return;
      }

      tableCounts.value = getTableActionCounts(table);
      tableCounts.value.set(rowId, actionsCount);
    };

    onMounted(publishActionsCount);
    onUpdated(publishActionsCount);
    onBeforeUnmount(() => tableCounts.value?.delete(rowId));

    const needsMenu = (count: number): boolean => count > MAX_INLINE_ACTIONS;

    return () => {
      const nodes = slots.default?.() ?? [];

      if (!newUiEnabled.value) {
        if (props.legacyClass === null) {
          return nodes;
        }

        return h("div", props.legacyClass ? { class: props.legacyClass } : {}, nodes);
      }

      actionsCount = countActions(nodes);

      const siblingNeedsMenu =
        tableCounts.value !== null &&
        [...tableCounts.value.values()].some((count) => needsMenu(count));

      if (actionsCount === 0 || (!needsMenu(actionsCount) && !siblingNeedsMenu)) {
        return h("div", { class: "ui-row-actions" }, nodes);
      }

      return h(
        UiDropdown,
        {
          class: "ui-row-actions-dropdown",
          label: t("Actions"),
          icon: ActionsMenuIcon,
          compact: true,
          iconOnly: true,
          closeOnContentClick: true,
        },
        { default: () => h("div", { class: "ui-row-actions__menu" }, nodes) },
      );
    };
  },
});
</script>
